import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axiosInstance, {
  clearAuthData,
  isJwtAuthFailure,
  getUserDetails,
  setAuthTokens,
  CheckUserExpiry,
} from "../../components/content/axiosConfig";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast.js";
import { openScopedAccount, clearScopedAccount, getScopedAccountId } from "../../utils/accountContext";
import { useClearScopedAccountOnMount } from "../../utils/useClearScopedAccountOnMount";
import {
  formatDisplayDateShort as formatEndDate,
  formatDisplayDateTime as formatDateTime,
} from "../../utils/formatDisplayDate";

const ACCOUNT_FILTERS = [
  { id: "all", label: "All accounts", shortLabel: "All", subscription: "", status: "" },
  { id: "running", label: "Package running", shortLabel: "Running", subscription: "running", status: "" },
  { id: "trial", label: "On trial", shortLabel: "Trial", subscription: "trial", status: "" },
  { id: "expired", label: "Expired", shortLabel: "Expired", subscription: "expired", status: "" },
  { id: "active", label: "Active", shortLabel: "Active", subscription: "", status: "active" },
  { id: "inactive", label: "Inactive", shortLabel: "Inactive", subscription: "", status: "inactive" },
];

const FILTER_STYLE = {
  all: { icon: "fa-users", color: "#495057", bg: "#f8f9fa" },
  running: { icon: "fa-box-open", color: "#0d9488", bg: "#ccfbf1" },
  trial: { icon: "fa-hourglass-half", color: "#4f46e5", bg: "#e0e7ff" },
  expired: { icon: "fa-calendar-times", color: "#dc3545", bg: "#fee2e2" },
  active: { icon: "fa-check-circle", color: "#198754", bg: "#d1e7dd" },
  inactive: { icon: "fa-user-clock", color: "#d97706", bg: "#fef3c7" },
};

const SUBSCRIPTION_FILTER_IDS = ["running", "trial", "expired"];
const STATUS_FILTER_IDS = ["active", "inactive"];

const cardRadius = { borderRadius: "14px" };

function buildAccountsPath(subscription, status) {
  const params = new URLSearchParams();
  if (subscription) params.set("subscription", subscription);
  if (status) params.set("status", status);
  const qs = params.toString();
  return `/company/accounts${qs ? `?${qs}` : ""}`;
}

function getFilterById(id) {
  return ACCOUNT_FILTERS.find((f) => f.id === id) || ACCOUNT_FILTERS[0];
}

function getActiveFilterId(subscription, status) {
  const match = ACCOUNT_FILTERS.find(
    (f) => f.subscription === subscription && f.status === status
  );
  return match ? match.id : "all";
}

function AccountFilterPill({ filter, isActive, onSelect }) {
  const style = FILTER_STYLE[filter.id] || FILTER_STYLE.all;
  return (
    <button
      type="button"
      className="accounts-filter-pill"
      aria-pressed={isActive}
      title={filter.label}
      onClick={() => onSelect(filter)}
      style={
        isActive
          ? {
              backgroundColor: style.bg,
              borderColor: style.color,
              color: style.color,
              boxShadow: `inset 0 0 0 1px ${style.color}`,
            }
          : undefined
      }
    >
      <i className={`fa ${style.icon} fa-fw me-1`} />
      {filter.shortLabel || filter.label}
    </button>
  );
}

function getPackageDisplay(row) {
  if (row.IsCompanySandbox === true || row.IsCompanySandbox === 1) {
    return { full: "Company Demo", short: "Demo" };
  }
  if (row.PackageName) {
    const full = String(row.PackageName).trim();
    const dashSplit = full.split(" - ");
    const short =
      dashSplit.length > 1 && dashSplit[0].length >= 3
        ? dashSplit[0].trim()
        : full.length > 28
          ? `${full.slice(0, 28).trim()}…`
          : full;
    return { full, short };
  }
  if (!row.EndDate) return null;
  const end = new Date(row.EndDate);
  end.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (today <= end) return { full: "Trial", short: "Trial" };
  return null;
}

function PackageCell({ row }) {
  const pkg = getPackageDisplay(row);
  if (!pkg) return <span className="text-muted">—</span>;
  const needsTooltip = pkg.full.length > pkg.short.length;
  return (
    <span className="accounts-package-cell" title={needsTooltip ? pkg.full : undefined}>
      {pkg.short}
    </span>
  );
}

function AccountsPager({ page, total, limit, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / limit));
  const indexOfFirstEntry = total === 0 ? 0 : (page - 1) * limit + 1;
  const indexOfLastEntry = Math.min(page * limit, total);

  if (total === 0) {
    return (
      <div className="d-flex align-items-center justify-content-between px-3 py-3 border-top">
        <span className="small text-muted">No entries</span>
      </div>
    );
  }

  const pageNumbers = [];
  const maxButtons = 7;
  let start = Math.max(1, page - Math.floor(maxButtons / 2));
  let end = Math.min(totalPages, start + maxButtons - 1);
  start = Math.max(1, end - maxButtons + 1);
  for (let i = start; i <= end; i += 1) {
    pageNumbers.push(i);
  }

  return (
    <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2 px-3 py-3 border-top">
      <span className="small text-muted text-center text-md-start">
        Showing {indexOfFirstEntry} to {indexOfLastEntry} of {total} entries
      </span>
      {totalPages > 1 ? (
        <ul className="pagination mb-0 justify-content-center">
          <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
            <button
              type="button"
              className="page-link"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </button>
          </li>
          {pageNumbers.map((n) => (
            <li key={n} className={`page-item ${n === page ? "active" : ""}`}>
              <button type="button" className="page-link" onClick={() => onPageChange(n)}>
                {n}
              </button>
            </li>
          ))}
          <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
            <button
              type="button"
              className="page-link"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  );
}

const ACCOUNTS_PAGE_STYLES = `
  .accounts-page {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 6.5rem);
  }
  .accounts-table-card {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 28rem;
  }
  .accounts-table-card > .card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: relative;
  }
  .accounts-table-scroll {
    flex: 1 1 auto;
    min-height: 22rem;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
  .accounts-table {
    min-width: 920px;
    width: 100%;
  }
  .accounts-table th,
  .accounts-table td {
    white-space: nowrap;
    vertical-align: middle;
  }
  .accounts-table td.col-package,
  .accounts-table th.col-package {
    white-space: normal;
  }
  .accounts-filter-toolbar {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 14px;
  }
  .accounts-filter-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.3rem 0.65rem;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1;
    color: #495057;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 999px;
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }
  .accounts-filter-pill .fa {
    font-size: 0.7rem;
  }
  .accounts-filter-pill:hover {
    background: #f8f9fa;
    border-color: #ced4da;
  }
  .accounts-filter-pill:focus-visible {
    outline: 2px solid #86b7fe;
    outline-offset: 2px;
  }
  .accounts-filter-divider {
    width: 1px;
    height: 1.25rem;
    background: #dee2e6;
    flex-shrink: 0;
  }
  .accounts-search-input {
    border-radius: 8px !important;
    min-height: 34px;
    font-size: 0.875rem;
  }
  .accounts-package-cell {
    display: inline-block;
    max-width: 11rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    cursor: default;
  }
  .accounts-table th.col-package,
  .accounts-table td.col-package {
    max-width: 11rem;
  }
  .accounts-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.65rem;
    border-radius: 999px;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1;
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
  }
  .accounts-mobile-list {
    padding: 0.85rem;
  }
  .accounts-mobile-card {
    border: 1px solid #eef0f2;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    margin-bottom: 0.75rem;
  }
  .accounts-mobile-card:last-child {
    margin-bottom: 0;
  }
  .accounts-mobile-card__body {
    padding: 0.95rem 1rem;
  }
  .accounts-mobile-card__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #212529;
    line-height: 1.3;
    word-break: break-word;
  }
  .accounts-mobile-card__title a,
  .accounts-mobile-card__title-link {
    color: inherit !important;
    text-decoration: none !important;
  }
  .accounts-mobile-card__title a:hover,
  .accounts-mobile-card__title-link:hover {
    color: #0d9488 !important;
  }
  .accounts-mobile-card__email {
    margin: 0.2rem 0 0;
    font-size: 0.8rem;
    color: #6c757d;
    word-break: break-all;
  }
  .accounts-mobile-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.55rem 0.75rem;
    margin-top: 0.85rem;
  }
  .accounts-mobile-meta__label {
    display: block;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6c757d;
    margin-bottom: 0.15rem;
  }
  .accounts-mobile-meta__value {
    font-size: 0.875rem;
    font-weight: 600;
    color: #212529;
    word-break: break-word;
  }
  .accounts-mobile-meta__value .accounts-package-cell {
    max-width: none;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    color: inherit;
  }
  .accounts-mobile-card__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    margin-top: 0.9rem;
    padding-top: 0.75rem;
    border-top: 1px solid #eef0f2;
  }
  .accounts-mobile-card__actions .btn {
    display: inline-flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    width: 100%;
    min-width: 0;
    border-radius: 8px !important;
    font-size: 0.78rem;
    font-weight: 600;
    white-space: nowrap !important;
    text-decoration: none !important;
    line-height: 1.2 !important;
    padding: 0.45rem 0.6rem !important;
    overflow: hidden;
  }
  .accounts-mobile-card__actions .btn i {
    margin: 0 !important;
    flex: 0 0 auto;
  }
  .accounts-mobile-card__actions .btn span {
    display: inline-block;
    white-space: nowrap !important;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  @media (max-width: 767.98px) {
    .accounts-table-card {
      min-height: 0;
    }
    .accounts-table-scroll {
      min-height: 0;
      overflow: visible;
    }
    .accounts-filter-divider {
      display: none;
    }
    .accounts-header {
      flex-direction: column;
      align-items: stretch !important;
    }
    .accounts-header__create {
      margin-left: 0 !important;
      width: 100%;
    }
    .accounts-header__create .btn {
      width: 100%;
    }
    .accounts-search-wrap {
      max-width: none !important;
      min-width: 0 !important;
      width: 100%;
      flex: 1 1 100% !important;
      margin-left: 0 !important;
    }
  }

  /* Tablet only (md–lg): card grid instead of cramped wide table — mobile/desktop unchanged */
  @media (min-width: 768px) and (max-width: 991.98px) {
    .accounts-table-card {
      min-height: 0;
    }
    .accounts-header {
      align-items: flex-start !important;
    }
    .accounts-header__create {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: flex-end;
      max-width: 100%;
    }
    .accounts-header__create .btn {
      white-space: nowrap;
    }
    .accounts-filter-toolbar .card-body {
      padding: 0.85rem 1rem;
    }
    .accounts-filter-toolbar .d-flex.flex-wrap {
      gap: 0.65rem;
    }
    .accounts-search-wrap {
      max-width: none !important;
      min-width: 0 !important;
      flex: 1 1 100% !important;
      margin-left: 0 !important;
    }
    .accounts-mobile-list {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.85rem;
      padding: 1rem;
    }
    .accounts-mobile-card {
      margin-bottom: 0;
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    .accounts-mobile-card__body {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 1.05rem 1.1rem;
    }
    .accounts-mobile-card__title {
      font-size: 1.05rem;
    }
    .accounts-mobile-meta {
      grid-template-columns: 1fr 1fr;
      gap: 0.65rem 1rem;
    }
    .accounts-mobile-card__actions {
      grid-template-columns: repeat(3, 1fr);
      margin-top: auto;
      padding-top: 0.85rem;
    }
    .accounts-mobile-list > .text-center,
    .accounts-mobile-list > .accounts-empty-state {
      grid-column: 1 / -1;
    }
  }

  /* Dark mode — match Donezo shell surfaces */
  body.dark-mode .accounts-filter-toolbar {
    background: #171d26;
    border-color: #2d3848;
  }
  body.dark-mode .accounts-filter-pill {
    color: #e5e7eb;
    background: #1f2733;
    border-color: #2d3848;
  }
  body.dark-mode .accounts-filter-pill:hover {
    background: #263041;
    border-color: #3d4b5f;
  }
  body.dark-mode .accounts-filter-divider {
    background: #2d3848;
  }
  body.dark-mode .accounts-search-input {
    background: #1f2733 !important;
    border-color: #2d3848 !important;
    color: #f3f4f6 !important;
  }
  body.dark-mode .accounts-search-input::placeholder {
    color: #6b7280;
  }
  body.dark-mode .accounts-count-badge {
    color: #e5e7eb;
    background: #1f2733;
    border-color: #2d3848;
  }
  body.dark-mode .accounts-table-card {
    background: #171d26;
    border-color: #2d3848;
  }
  body.dark-mode .accounts-mobile-card {
    background: #1f2733;
    border-color: #2d3848;
    box-shadow: none;
  }
  body.dark-mode .accounts-mobile-card__title {
    color: #f3f4f6;
  }
  body.dark-mode .accounts-mobile-card__title a:hover,
  body.dark-mode .accounts-mobile-card__title-link:hover {
    color: #2dd4bf !important;
  }
  body.dark-mode .accounts-mobile-card__email {
    color: #9ca3af;
  }
  body.dark-mode .accounts-mobile-meta__label {
    color: #9ca3af;
  }
  body.dark-mode .accounts-mobile-meta__value {
    color: #f3f4f6;
  }
  body.dark-mode .accounts-mobile-card__actions {
    border-top-color: #2d3848;
  }
  body.dark-mode .accounts-mobile-card__actions .btn-outline-primary {
    color: #2dd4bf;
    border-color: rgba(45, 212, 191, 0.45);
    background: transparent;
  }
  body.dark-mode .accounts-mobile-card__actions .btn-outline-secondary {
    color: #e5e7eb;
    border-color: #3d4b5f;
    background: transparent;
  }
  body.dark-mode .accounts-mobile-card__actions .btn-outline-danger {
    color: #f87171;
    border-color: rgba(248, 113, 113, 0.45);
    background: transparent;
  }
  body.dark-mode .accounts-empty-state {
    background: rgba(45, 212, 191, 0.08) !important;
  }
`;

const CompanyAccounts = () => {
  useClearScopedAccountOnMount();
  const location = useLocation();
  const history = useHistory();
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [detachingId, setDetachingId] = useState(null);
  const [converting, setConverting] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteActionId, setInviteActionId] = useState(null);
  const [claimBusy, setClaimBusy] = useState(false);
  const limit = 10;

  const isCompanyOwner = useMemo(() => {
    const u = getUserDetails() || {};
    return Boolean(u.IsCompanyOwner || u.isCompanyOwner);
  }, []);

  const { subscription, status, activeFilterId, activeFilterLabel } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const nextSubscription = (params.get("subscription") || "").trim().toLowerCase();
    const nextStatus = (params.get("status") || "").trim().toLowerCase();
    const filterId = getActiveFilterId(nextSubscription, nextStatus);
    const filter = ACCOUNT_FILTERS.find((f) => f.id === filterId) || ACCOUNT_FILTERS[0];
    return {
      subscription: filter.subscription,
      status: filter.status,
      activeFilterId: filter.id,
      activeFilterLabel: filter.label,
    };
  }, [location.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { search, page, limit };
      if (subscription) params.subscription = subscription;
      if (status) params.status = status;
      const res = await axiosInstance.get("api/company/accounts", { params });
      const rows = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setAccounts(rows);
      setTotal(res.data?.total ?? rows.length);
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(error.response?.data?.message || "Failed to load accounts");
    } finally {
      setIsLoading(false);
    }
  }, [search, page, subscription, status]);

  const loadInvites = useCallback(async () => {
    try {
      const res = await axiosInstance.get("api/company/accounts/invites");
      setPendingInvites(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* non-fatal on list page */
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  useEffect(() => {
    setPage(1);
  }, [subscription, status, search]);

  const applyFilter = (filter) => {
    history.push(buildAccountsPath(filter.subscription, filter.status));
  };

  const handleConvertToPondOwner = async () => {
    if (converting) return;
    const result = await Swal.fire({
      title: "Convert to pond owner?",
      html: `
        <p class="text-start mb-2">This will:</p>
        <ul class="text-start small mb-2">
          <li>Remove all managed accounts from your consultancy (they keep their ponds and become independent)</li>
          <li>End your company login permanently</li>
          <li>Create your personal pond owner account with a <strong>14-day trial</strong></li>
        </ul>
        <p class="text-start small text-muted mb-0">This can only be done once and cannot be undone.</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Convert to pond owner",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d9488",
      focusCancel: true,
    });
    if (!result.isConfirmed) return;

    setConverting(true);
    try {
      const res = await axiosInstance.post("api/company/convert-to-pond-owner", {
        confirm: true,
      });
      const { token, refreshToken, User } = res.data || {};
      if (!token || !User) {
        showErrorToast("Conversion succeeded but session could not be started");
        return;
      }
      clearScopedAccount();
      clearAuthData();
      setAuthTokens(token, refreshToken, User);
      showSuccessToast(
        res.data?.message || "Converted to pond owner with a 14-day trial"
      );
      await CheckUserExpiry();
      history.push("/");
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(
        error.response?.data?.message || "Failed to convert to pond owner"
      );
    } finally {
      setConverting(false);
    }
  };

  const handleOpenAccount = (row) => {
    openScopedAccount(
      {
        id: row.id,
        name: row.FullName,
        email: row.Email,
      },
      { history }
    );
  };

  const handleStatusToggle = async (row) => {
    try {
      await axiosInstance.post(`api/company/accounts/${row.id}/status`);
      showSuccessToast("Status updated");
      loadAccounts();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Could not update status");
    }
  };

  const handleDelete = async (row) => {
    if (deletingId != null || detachingId != null) return;

    const isSandbox = row.IsCompanySandbox === true || row.IsCompanySandbox === 1;
    let result;
    try {
      result = await Swal.fire({
        title: isSandbox ? "Delete Demo account?" : "Delete this account?",
        html: isSandbox
          ? `This permanently removes <strong>${row.FullName}</strong> (${row.Email}) and its ponds/data. The Demo account will not be recreated automatically.`
          : `This permanently removes the account and personal information for <strong>${row.FullName}</strong> (${row.Email}).`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: isSandbox ? "Yes, delete Demo" : "Yes, delete account",
        cancelButtonText: "Cancel",
        focusCancel: true,
      });
    } catch (error) {
      showErrorToast("Could not open the confirmation dialog.");
      console.error("Delete confirmation error:", error);
      return;
    }
    if (!result.isConfirmed) return;

    setDeletingId(row.id);
    try {
      const res = await axiosInstance.delete(`api/company/accounts/${row.id}`);
      showSuccessToast(res.data?.message || "Account deleted");
      const scopedId = getScopedAccountId();
      if (scopedId != null && Number(scopedId) === Number(row.id)) {
        clearScopedAccount();
      }
      loadAccounts();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDetach = async (row) => {
    if (deletingId != null || detachingId != null) return;
    if (row.IsCompanySandbox === true || row.IsCompanySandbox === 1) {
      showErrorToast(
        "The Demo account cannot be removed from your consultancy. It is for testing."
      );
      return;
    }

    let result;
    try {
      result = await Swal.fire({
        title: "Remove from consultancy?",
        html: `<p class="mb-2">Remove <strong>${row.FullName}</strong> (${row.Email}) from your consultancy?</p>
          <p class="mb-0 text-muted small">They keep their ponds and data, and will manage their own subscription going forward. Company billing for this account will stop.</p>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#0d9488",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, remove from consultancy",
        cancelButtonText: "Cancel",
        focusCancel: true,
      });
    } catch (error) {
      showErrorToast("Could not open the confirmation dialog.");
      console.error("Detach confirmation error:", error);
      return;
    }
    if (!result.isConfirmed) return;

    setDetachingId(row.id);
    try {
      const res = await axiosInstance.post(`api/company/accounts/${row.id}/detach`);
      showSuccessToast(
        res.data?.message ||
          "Account removed from consultancy. They now manage their own subscription."
      );
      loadAccounts();
    } catch (error) {
      showErrorToast(
        error.response?.data?.message || "Failed to remove account from consultancy"
      );
    } finally {
      setDetachingId(null);
    }
  };

  const handleClaimExisting = async () => {
    if (claimBusy || converting || detachingId != null || deletingId != null) return;

    const emailResult = await Swal.fire({
      title: "Claim existing pond owner",
      html: `<p class="text-start small mb-2">Enter the pond owner's email. They must accept before the account is linked. Their current package and end date stay the same; you handle renewals later.</p>`,
      input: "email",
      inputPlaceholder: "owner@example.com",
      showCancelButton: true,
      confirmButtonText: "Look up",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d9488",
      inputValidator: (value) => {
        if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) {
          return "Enter a valid email";
        }
        return null;
      },
    });
    if (!emailResult.isConfirmed) return;
    const email = String(emailResult.value || "").trim();

    setClaimBusy(true);
    try {
      const { data: owner } = await axiosInstance.get("api/company/accounts/lookup", {
        params: { email },
      });

      const confirm = await Swal.fire({
        title: "Send claim invitation?",
        html: `
          <p class="text-start mb-2"><strong>${owner.FullName || "Owner"}</strong> (${owner.Email})</p>
          <p class="text-start small text-muted mb-0">
            Package: ${owner.PackageName || "—"} · End: ${
              owner.EndDate ? formatEndDate(owner.EndDate) : "—"
            } · Licensed ponds: ${owner.LicensedPondCount ?? "—"}
          </p>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Send claim",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#0d9488",
      });
      if (!confirm.isConfirmed) return;

      const res = await axiosInstance.post("api/company/accounts/claim", { email });
      showSuccessToast(res.data?.message || "Claim invitation sent");
      await loadInvites();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Could not claim account");
    } finally {
      setClaimBusy(false);
    }
  };

  const handleInviteRespond = async (invite, accept) => {
    if (inviteActionId != null) return;
    const name = invite.Fisher?.FullName || invite.Fisher?.Email || "this account";
    const isOwnerRequest = invite.InitiatedBy === "fisher";

    if (accept && !isOwnerRequest) {
      showErrorToast("Only the pond owner can accept a claim you sent");
      return;
    }

    const title = accept
      ? `Accept join request from ${name}?`
      : isOwnerRequest
        ? `Decline join request from ${name}?`
        : `Cancel claim for ${name}?`;

    const result = await Swal.fire({
      title,
      text: accept
        ? "Their package and end date stay the same. You will manage renewals."
        : undefined,
      icon: accept ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: accept ? "Accept" : isOwnerRequest ? "Decline" : "Cancel claim",
      cancelButtonText: "Back",
      confirmButtonColor: accept ? "#0d9488" : "#dc3545",
      focusCancel: true,
    });
    if (!result.isConfirmed) return;

    setInviteActionId(invite.id);
    try {
      const res = await axiosInstance.post(
        `api/company/accounts/invites/${invite.id}/respond`,
        { accept: !!accept }
      );
      showSuccessToast(res.data?.message || "Updated");
      await Promise.all([loadInvites(), loadAccounts()]);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to update invitation");
    } finally {
      setInviteActionId(null);
    }
  };

  if (isLoading && accounts.length === 0 && deletingId == null && detachingId == null) {
    return <Preloader />;
  }

  return (
    <div className="accounts-page">
      <style>{ACCOUNTS_PAGE_STYLES}</style>

      <div className="d-flex align-items-center mb-3 flex-wrap gap-2 accounts-header">
        <div>
          <ul className="breadcrumb mb-2">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Accounts</li>
          </ul>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
            <h1 className="page-header mb-0">Accounts</h1>
            <span className="accounts-count-badge" aria-label="Total accounts">
              {isLoading ? "…" : total}
            </span>
            {!isLoading && activeFilterId !== "all" ? (
              <span
                className="small fw-medium"
                style={{ color: FILTER_STYLE[activeFilterId]?.color }}
              >
                {activeFilterLabel}
              </span>
            ) : null}
          </div>
          <p className="text-muted small mb-0">
            Pond owner accounts you manage and pay for — filter by subscription or account status.
          </p>
        </div>
        <div className="ms-auto accounts-header__create d-flex flex-wrap gap-2">
          {isCompanyOwner ? (
            <button
              type="button"
              className="btn btn-outline-teal"
              style={{ borderColor: "#0d9488", color: "#0d9488" }}
              disabled={converting || !!detachingId || !!deletingId}
              onClick={handleConvertToPondOwner}
              title="Convert your company login into a personal pond owner account"
            >
              <i
                className={`fa ${converting ? "fa-spinner fa-spin" : "fa-exchange"} me-1`}
                aria-hidden="true"
              />
              {converting ? "Converting…" : "Convert to pond owner"}
            </button>
          ) : null}
          <Link
            to="/company/accounts/create"
            className="btn btn-primary"
            style={{ pointerEvents: claimBusy ? "none" : undefined }}
          >
            <i className="fa fa-plus me-1" /> Create account
          </Link>
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={claimBusy || converting || !!detachingId || !!deletingId}
            onClick={handleClaimExisting}
            title="Invite an existing pond owner to join your consultancy"
          >
            <i
              className={`fa ${claimBusy ? "fa-spinner fa-spin" : "fa-user-plus"} me-1`}
              aria-hidden="true"
            />
            {claimBusy ? "Working…" : "Claim existing account"}
          </button>
        </div>
      </div>

      {pendingInvites.length > 0 ? (
        <div className="card border-0 shadow-sm mb-3" style={cardRadius}>
          <div className="card-body">
            <h2 className="h6 fw-semibold mb-3">Pending invitations</h2>
            <div className="row g-3">
              {pendingInvites.map((inv) => {
                const isOwnerRequest = inv.InitiatedBy === "fisher";
                const fisher = inv.Fisher || {};
                return (
                  <div className="col-md-6 col-lg-4" key={inv.id}>
                    <div className="border rounded-3 p-3 h-100 d-flex flex-column">
                      <div className="small text-muted mb-1">
                        {isOwnerRequest ? "Owner join request" : "Your claim (awaiting owner)"}
                      </div>
                      <div className="fw-semibold">{fisher.FullName || "Pond owner"}</div>
                      <div className="small text-muted mb-2">{fisher.Email}</div>
                      {inv.Message ? (
                        <p className="small border-start ps-2 mb-3 text-secondary">{inv.Message}</p>
                      ) : null}
                      <div className="mt-auto d-flex flex-wrap gap-2">
                        {isOwnerRequest ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-success rounded-pill px-3"
                              disabled={inviteActionId === inv.id}
                              onClick={() => handleInviteRespond(inv, true)}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                              disabled={inviteActionId === inv.id}
                              onClick={() => handleInviteRespond(inv, false)}
                            >
                              Decline
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger rounded-pill px-3"
                            disabled={inviteActionId === inv.id}
                            onClick={() => handleInviteRespond(inv, false)}
                          >
                            Cancel claim
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      <div className="card border-0 shadow-sm mb-3 accounts-filter-toolbar" style={cardRadius}>
        <div className="card-body py-2 px-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="d-flex flex-wrap align-items-center gap-1">
              <AccountFilterPill
                filter={getFilterById("all")}
                isActive={activeFilterId === "all"}
                onSelect={applyFilter}
              />
              <span className="accounts-filter-divider" aria-hidden="true" />
              {SUBSCRIPTION_FILTER_IDS.map((id) => (
                <AccountFilterPill
                  key={id}
                  filter={getFilterById(id)}
                  isActive={activeFilterId === id}
                  onSelect={applyFilter}
                />
              ))}
              <span className="accounts-filter-divider" aria-hidden="true" />
              {STATUS_FILTER_IDS.map((id) => (
                <AccountFilterPill
                  key={id}
                  filter={getFilterById(id)}
                  isActive={activeFilterId === id}
                  onSelect={applyFilter}
                />
              ))}
            </div>

            <div
              className="position-relative flex-grow-1 ms-md-auto accounts-search-wrap"
              style={{ minWidth: "160px", maxWidth: "280px" }}
            >
              <div
                className="position-absolute top-50 translate-middle-y text-muted ps-2"
                style={{ zIndex: 2, fontSize: "0.8rem" }}
              >
                <i className="fa fa-search opacity-50" />
              </div>
              <input
                type="search"
                className="form-control form-control-sm accounts-search-input border bg-light ps-4"
                placeholder="Search name or email…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <div className="d-flex align-items-center gap-2 ms-auto ms-md-0 text-nowrap">
              {activeFilterId !== "all" || searchInput ? (
                <Link
                  to="/company/accounts"
                  className="small text-decoration-none"
                  onClick={() => setSearchInput("")}
                >
                  Clear
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm accounts-table-card" style={cardRadius}>
        <div className="card-body p-0">
          {deletingId != null || detachingId != null ? (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
              style={{ zIndex: 5 }}
            >
              <div className="text-muted">
                <i className="fa fa-spinner fa-spin me-2" />
                {detachingId != null
                  ? "Removing from consultancy…"
                  : "Deleting account…"}
              </div>
            </div>
          ) : null}

          {/* Desktop table (lg+) — tablet uses cards below */}
          <div className="d-none d-lg-block table-responsive mb-0 position-relative accounts-table-scroll">
            <table className="table table-hover table-panel align-middle mb-0 accounts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th className="col-package">Package</th>
                  <th>Pond license</th>
                  <th>End date</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      Loading…
                    </td>
                  </tr>
                ) : accounts.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      No accounts match this filter.
                    </td>
                  </tr>
                ) : (
                  accounts.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Link to={`/company/accounts/${row.id}`}>{row.FullName}</Link>
                        {row.IsCompanySandbox === true || row.IsCompanySandbox === 1 ? (
                          <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill ms-2">
                            Demo
                          </span>
                        ) : null}
                      </td>
                      <td>{row.Email}</td>
                      <td className="col-package">
                        <PackageCell row={row} />
                      </td>
                      <td>{row.LicensedPondCount ?? 1}</td>
                      <td>{formatEndDate(row.EndDate)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 text-decoration-none"
                          title="Toggle status"
                          onClick={() => handleStatusToggle(row)}
                        >
                          {row.Status ? (
                            <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px" /> Active
                            </span>
                          ) : (
                            <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px" /> Inactive
                            </span>
                          )}
                        </button>
                      </td>
                      <td>{formatDateTime(row.createdAt)}</td>
                      <td>
                        <span>
                          <button
                            type="button"
                            className="btn btn-link p-0 border-0 text-primary"
                            title="Open account (sampling)"
                            disabled={deletingId != null || detachingId != null}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenAccount(row);
                            }}
                          >
                            <i className="fa fa-sign-in fs-13px fa-fw me-5px" />
                          </button>
                        </span>
                        <span>
                          <Link to={`/company/staff/${row.id}`} title="Staff">
                            {" "}
                            <i className="fa fa-user fs-13px fa-fw me-5px" />
                          </Link>
                        </span>
                        <span>
                          <Link to={`/company/accounts/${row.id}`} title="Edit">
                            {" "}
                            <i className="fa fa-pencil fs-13px fa-fw me-5px" />
                          </Link>
                        </span>
                        <span>
                          {!(row.IsCompanySandbox === true || row.IsCompanySandbox === 1) ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 border-0 text-teal"
                              title="Remove from consultancy"
                              style={{ color: "#0d9488" }}
                              disabled={deletingId != null || detachingId != null}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDetach(row);
                              }}
                            >
                              <i className="fa fa-unlink fs-13px fa-fw me-5px" />
                            </button>
                          ) : null}
                        </span>
                        &nbsp;&nbsp;
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 text-danger"
                          title={
                            row.IsCompanySandbox === true || row.IsCompanySandbox === 1
                              ? "Delete Demo account"
                              : "Delete account"
                          }
                          disabled={deletingId != null || detachingId != null}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(row);
                          }}
                        >
                          <i className="fa fa-trash fs-13px fa-fw me-5px" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile + tablet cards */}
          <div className="d-lg-none accounts-mobile-list position-relative">
            {isLoading ? (
              <div className="text-center text-muted py-5">Loading…</div>
            ) : accounts.length === 0 ? (
              <div
                className="text-center rounded-3 py-4 px-3 accounts-empty-state"
                style={{ backgroundColor: "rgba(var(--bs-primary-rgb), 0.08)" }}
              >
                <p className="fw-semibold text-dark mb-1">No accounts found</p>
                <p className="small text-muted mb-3">
                  {searchInput || activeFilterId !== "all"
                    ? "Try another search or filter."
                    : "Your Demo account appears here after signup. Create paid client accounts when ready."}
                </p>
                {!searchInput && activeFilterId === "all" ? (
                  <Link to="/company/accounts/create" className="btn btn-primary btn-sm">
                    Create account
                  </Link>
                ) : null}
              </div>
            ) : (
              accounts.map((row) => (
                <article key={row.id} className="accounts-mobile-card">
                  <div className="accounts-mobile-card__body">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div className="min-w-0">
                        <h2 className="accounts-mobile-card__title">
                          <Link
                            to={`/company/accounts/${row.id}`}
                            className="accounts-mobile-card__title-link"
                          >
                            {row.FullName}
                          </Link>
                          {row.IsCompanySandbox === true || row.IsCompanySandbox === 1 ? (
                            <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill ms-2">
                              Demo
                            </span>
                          ) : null}
                        </h2>
                        <p className="accounts-mobile-card__email">{row.Email}</p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-link p-0 border-0 text-decoration-none flex-shrink-0"
                        title="Toggle status"
                        onClick={() => handleStatusToggle(row)}
                      >
                        {row.Status ? (
                          <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                            <i className="fa fa-circle fs-9px fa-fw me-5px" /> Active
                          </span>
                        ) : (
                          <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                            <i className="fa fa-circle fs-9px fa-fw me-5px" /> Inactive
                          </span>
                        )}
                      </button>
                    </div>

                    <div className="accounts-mobile-meta">
                      <div>
                        <span className="accounts-mobile-meta__label">Package</span>
                        <div className="accounts-mobile-meta__value">
                          <PackageCell row={row} />
                        </div>
                      </div>
                      <div>
                        <span className="accounts-mobile-meta__label">Pond license</span>
                        <div className="accounts-mobile-meta__value">
                          {row.LicensedPondCount ?? 1}
                        </div>
                      </div>
                      <div>
                        <span className="accounts-mobile-meta__label">End date</span>
                        <div className="accounts-mobile-meta__value">
                          {formatEndDate(row.EndDate) || "—"}
                        </div>
                      </div>
                      <div>
                        <span className="accounts-mobile-meta__label">Created</span>
                        <div className="accounts-mobile-meta__value">
                          {formatDateTime(row.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div className="accounts-mobile-card__actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={deletingId != null || detachingId != null}
                        onClick={() => handleOpenAccount(row)}
                      >
                        <i className="fa fa-sign-in" aria-hidden />
                        <span>Open</span>
                      </button>
                      <Link
                        to={`/company/accounts/${row.id}`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="fa fa-pencil" aria-hidden />
                        <span>Edit</span>
                      </Link>
                      <Link
                        to={`/company/staff/${row.id}`}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        <i className="fa fa-user" aria-hidden />
                        <span>Staff</span>
                      </Link>
                      {!(row.IsCompanySandbox === true || row.IsCompanySandbox === 1) ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{ color: "#0d9488", borderColor: "#99f6e4" }}
                          disabled={deletingId != null || detachingId != null}
                          onClick={() => handleDetach(row)}
                        >
                          <i className="fa fa-unlink" aria-hidden />
                          <span>Release</span>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={deletingId != null || detachingId != null}
                        onClick={() => handleDelete(row)}
                      >
                        <i className="fa fa-trash" aria-hidden />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>

          <AccountsPager page={page} total={total} limit={limit} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
};

export default CompanyAccounts;
